import React from 'react';
import { useTranslation } from 'react-i18next';
import { TradeType } from '../../types/tradeBooking';

// Date convention option keys (will be translated)
const TRADE_DATE_OPTION_KEYS = ['today', 'todayPlus1', 'todayMinus1'];
const VALUE_DATE_OPTION_KEYS = ['tradeDateOption', 'tradeDatePlus1', 'tradeDatePlus2'];
const EXPIRY_DATE_OPTION_KEYS = ['valueDateOption', 'valueDatePlus1', 'valueDatePlus2'];
const PAYMENT_DATE_OPTION_KEYS = ['expiryDateOption', 'expiryDatePlus1', 'expiryDatePlus2'];

interface DateConvention {
  id: string;
  tradeType: TradeType;
  currencyPair: string; // e.g., "USD/CLP", "EUR/CLP", "ALL" for default
  // Default conventions (always shown)
  defaultTradeDate: string; // e.g., "today", "todayPlus2"
  defaultValueDate: string; // e.g., "tradeDateOption", "tradeDatePlus2"
  defaultExpiryDate: string; // e.g., "valueDateOption", "notApplicable"
  defaultPaymentDate: string; // e.g., "expiryDateOption", "expiryDatePlus1"
  // Business days flag
  useBusinessDays: boolean; // If true, skip weekends and holidays
  // Override flag
  hasOverride: boolean;
  // Override values (only used if hasOverride is true)
  overrideTradeDate?: string;
  overrideValueDate?: string;
  overrideExpiryDate?: string;
  overridePaymentDate?: string;
}

interface DatesSettingsModalProps {
  isOpen: boolean;
  conventions: DateConvention[];
  onClose: () => void;
  onSave: () => void;
  onResetDefaults: () => void;
  onAddConvention: () => void;
  onDeleteConvention: (id: string) => void;
  onToggleOverride: (id: string) => void;
  onUpdateConvention: (id: string, field: string, value: any) => void;
}

const DatesSettingsModal: React.FC<DatesSettingsModalProps> = ({
  isOpen,
  conventions,
  onClose,
  onSave,
  onResetDefaults,
  onAddConvention,
  onDeleteConvention,
  onToggleOverride,
  onUpdateConvention,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog dates-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('tradeBooking.settings.datesTitle')}</h2>
        </div>

        <div className="modal-body dates-modal-body">
          <div className="dates-modal-header">
            <p>{t('tradeBooking.settings.datesDescription')}</p>
          </div>

          <div className="dates-table-container">
            <table className="dates-table">
              <thead>
                <tr>
                  <th rowSpan={2} className="main-header" style={{width: '10%'}}>{t('tradeBooking.tradeType.label')}</th>
                  <th rowSpan={2} className="main-header" style={{width: '10%'}}>{t('tradeBooking.dates.currencyPair')}</th>
                  <th colSpan={4} className="main-header" style={{width: '32%'}}>
                    {t('tradeBooking.dates.defaultConventions')}
                  </th>
                  <th rowSpan={2} className="main-header toggle-header" style={{width: '8%'}}>{t('tradeBooking.dates.override')}</th>
                  <th colSpan={4} className="main-header" style={{width: '32%'}}>
                    {t('tradeBooking.dates.userOverrides')}
                  </th>
                </tr>
                <tr>
                  <th className="field-header date-col">{t('tradeBooking.administrative.tradeDate')}</th>
                  <th className="field-header date-col">{t('tradeBooking.spotFields.valueDate')}</th>
                  <th className="field-header date-col">{t('tradeBooking.spotFields.expiryDate')}</th>
                  <th className="field-header date-col">{t('tradeBooking.spotFields.paymentDate')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.administrative.tradeDate')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.spotFields.valueDate')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.spotFields.expiryDate')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.spotFields.paymentDate')}</th>
                </tr>
              </thead>
              <tbody>
                {conventions.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="empty-state">
                      {t('tradeBooking.dates.noConventions')}
                    </td>
                  </tr>
                ) : (
                  conventions.map((convention) => (
                    <tr key={convention.id}>
                      {/* Trade Type - Read Only */}
                      <td className="readonly-cell">
                        {t(`tradeBooking.tradeType.${convention.tradeType.toLowerCase()}`)}
                      </td>

                      {/* Currency Pair - Read Only */}
                      <td className="readonly-cell">
                        {convention.currencyPair === 'All Others'
                          ? t('tradeBooking.dates.allOthers')
                          : convention.currencyPair
                        }
                      </td>

                      {/* Default Conventions - Read Only */}
                      <td className="readonly-cell">{t(`tradeBooking.dates.${convention.defaultTradeDate}`)}</td>
                      <td className="readonly-cell">{t(`tradeBooking.dates.${convention.defaultValueDate}`)}</td>
                      <td className="readonly-cell">{t(`tradeBooking.dates.${convention.defaultExpiryDate}`)}</td>
                      <td className="readonly-cell">{t(`tradeBooking.dates.${convention.defaultPaymentDate}`)}</td>

                      {/* Override Toggle */}
                      <td className="toggle-cell">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={convention.hasOverride}
                            onChange={() => onToggleOverride(convention.id)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </td>

                      {/* Override Values - Only editable when toggle is ON */}
                      {/* Trade Date Override */}
                      <td className={!convention.hasOverride ? 'disabled-cell' : ''}>
                        <select
                          className="table-select override-input"
                          value={convention.overrideTradeDate || ''}
                          onChange={(e) => onUpdateConvention(convention.id, 'overrideTradeDate', e.target.value)}
                          disabled={!convention.hasOverride}
                        >
                          <option value="">{convention.hasOverride ? t('tradeBooking.dates.selectOption') : ''}</option>
                          {TRADE_DATE_OPTION_KEYS.map(key => (
                            <option key={key} value={key}>{t(`tradeBooking.dates.${key}`)}</option>
                          ))}
                        </select>
                      </td>

                      {/* Value Date Override */}
                      <td className={!convention.hasOverride ? 'disabled-cell' : ''}>
                        <select
                          className="table-select override-input"
                          value={convention.overrideValueDate || ''}
                          onChange={(e) => onUpdateConvention(convention.id, 'overrideValueDate', e.target.value)}
                          disabled={!convention.hasOverride}
                        >
                          <option value="">{convention.hasOverride ? t('tradeBooking.dates.selectOption') : ''}</option>
                          {VALUE_DATE_OPTION_KEYS.map(key => (
                            <option key={key} value={key}>{t(`tradeBooking.dates.${key}`)}</option>
                          ))}
                        </select>
                      </td>

                      {/* Expiry Date Override - Disabled for Forward */}
                      <td className={!convention.hasOverride || convention.tradeType === TradeType.FORWARD ? 'disabled-cell' : ''}>
                        <select
                          className="table-select override-input"
                          value={convention.overrideExpiryDate || ''}
                          onChange={(e) => onUpdateConvention(convention.id, 'overrideExpiryDate', e.target.value)}
                          disabled={!convention.hasOverride || convention.tradeType === TradeType.FORWARD}
                        >
                          <option value="">
                            {convention.tradeType === TradeType.FORWARD
                              ? t('tradeBooking.dates.setAtTradeLevel')
                              : (convention.hasOverride ? t('tradeBooking.dates.selectOption') : '')
                            }
                          </option>
                          {convention.tradeType === TradeType.SPOT && EXPIRY_DATE_OPTION_KEYS.map(key => (
                            <option key={key} value={key}>{t(`tradeBooking.dates.${key}`)}</option>
                          ))}
                        </select>
                      </td>

                      {/* Payment Date Override */}
                      <td className={!convention.hasOverride ? 'disabled-cell' : ''}>
                        <select
                          className="table-select override-input"
                          value={convention.overridePaymentDate || ''}
                          onChange={(e) => onUpdateConvention(convention.id, 'overridePaymentDate', e.target.value)}
                          disabled={!convention.hasOverride}
                        >
                          <option value="">{convention.hasOverride ? t('tradeBooking.dates.selectOption') : ''}</option>
                          {PAYMENT_DATE_OPTION_KEYS.map(key => (
                            <option key={key} value={key}>{t(`tradeBooking.dates.${key}`)}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('tradeBooking.settings.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={onSave}>
            {t('tradeBooking.settings.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatesSettingsModal;
export type { DateConvention };
